import { Helmet } from 'react-helmet';
import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Tick from 'assets/icons/tick.svg?react';
import {
  useClientQuery,
  useActiveLoanQuery,
  useLegacyLoginAuth,
} from 'hooks/webapi';
import Card from 'components/Card/Card';
import { numberToLocale } from 'utils/translate';
import Typography from 'components/Typography/Typography';
import Loan from 'components/Calculator/ActiveLoan';
import { useKeycloakStore } from 'hooks/zustandStore';
import { Button, Placeholder } from '../../../components';
import styles from './Approved.module.scss';

export default function Approved() {
  const { t } = useTranslation('approved');
  const [pending, setPending] = useState(false);
  const { data: client } = useClientQuery(true);
  const { data: activeLoan } = useActiveLoanQuery();

  const { crossLoginRedirect } = useLegacyLoginAuth();

  const isLegacyLogin = useKeycloakStore((state) => state.isLegacyLogin);
  const isLegacyLoginLoading = useKeycloakStore(
    (state) => state.isLegacyLoginLoading,
  );

  const isAuthModeResolving = isLegacyLoginLoading || isLegacyLogin === null;

  const handleGoToAccount = async () => {
    if (isAuthModeResolving) return;
    setPending(true);
    if (isLegacyLogin) {
      await crossLoginRedirect();
    } else {
      const redirectURL = `${import.meta.env.VITE_DASHBOARD_URL}`;
      sessionStorage.clear();

      window.location.assign(redirectURL);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Card className={styles.card} dataTest="approved-mainCard-layout">
          <div className={styles.check}>
            <Tick className={styles.icon} data-test="approved-mainCard-icon" />
          </div>

          <Typography
            kind="h1"
            size="xl"
            className={styles.title}
            fontWeight={700}
            dataTest="approved-mainCard-title"
          >
            {t('title')}
          </Typography>

          <Typography
            className={styles.description}
            size="s"
            dataTest="approved-mainCard-description"
          >
            {t('description')}
          </Typography>

          <Placeholder
            loading={!activeLoan?.amount}
            placeholder="00 000"
            dataTest="approved-mainCard-amount"
          >
            <div
              className={styles.amount}
              data-test="approved-mainCard-amount-value"
            >
              {numberToLocale(activeLoan?.amount)}
              <span>Kč</span>
            </div>
          </Placeholder>

          <div className={styles.cell}>
            <Loan
              className={styles.offer}
              loading={!activeLoan}
              loan={activeLoan}
            />
          </div>

          <Button
            onClick={handleGoToAccount}
            pending={pending || isAuthModeResolving}
            dataTest="approved-submit-btn"
          >
            {t('go_to_account')}
          </Button>
        </Card>
        {client?.id && (
          <Card
            className={classNames([styles.card, styles.congratulation])}
            dataTest="approved-congratulation-card"
          >
            <Typography
              kind="h1"
              size="xxl"
              color="blue"
              centered
              className={styles.title}
              fontWeight={700}
              dataTest="approved-congratulation-title"
            >
              {t('congratulation.title')}
            </Typography>

            <Typography
              className={styles.description}
              centered
              size="s2"
              dataTest="approved-congratulation-text"
            >
              {t('congratulation.text')}
            </Typography>

            <div className={styles.approvedBtn}>
              <Button
                pending={pending}
                external
                wide
                to={`${import.meta.env.VITE_TYPE_FORM}#user_id=${
                  client?.number
                }`}
                dataTest="approved-congratulation-btn"
              >
                {t('congratulation.btn')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
